import { FiPlusCircle } from 'react-icons/fi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AddEmployeeStatusDetails from './AddEmployeeStatusDetails';

import { getUniqueKey } from 'utils/string';
import { getMonthsLaterDate } from 'utils/date';
import { FIXED_TERM_CONTRACT, PROBATION } from 'constants/employeeStatus';

import { getCheckIn } from '../../../components/employeeForm/forms/leapfrogHistory/components/EmployeeStatusDetails';

interface EmployeeStatusDetail {
  id?: number;
  keyId: number;
  category: string;
  date?: string;
}

type EmployeeStatusDetailStateAction = React.SetStateAction<EmployeeStatusDetail[]>;

interface EmployeeStatusDetailsProps {
  values: { engagementStatusId?: number; engagementStatusName: string; transitionDate: Date | string };
  setFieldValue: (field: string, value: Array<{ id?: number; category: string; date?: string }>) => void;
  employee: { currentTransitionDate: string };
  currentFiscalYear: { startDate: string; endDate: string };
  touched: { empStatusDetails?: Array<{ category?: boolean; date?: boolean }> };
  errors: { empStatusDetails?: Array<{ category?: string; date?: string }> };
  submitCount: number;
}

const EmployeeStatusDetails: React.FC<EmployeeStatusDetailsProps> = ({
  values,
  setFieldValue,
  employee,
  currentFiscalYear,
  touched,
  errors,
  submitCount,
}) => {
  const getDefaultValue = useCallback(
    (name: string) => {
      if (name === PROBATION) {
        return [
          {
            keyId: getUniqueKey(),
            category: 'Mid Probation',
            date: getMonthsLaterDate(values?.transitionDate, 3),
          },
          {
            keyId: getUniqueKey(),
            category: 'End Probation',
            date: getMonthsLaterDate(values?.transitionDate, 6),
          },
        ];
      }

      if (name === FIXED_TERM_CONTRACT) {
        return [
          {
            keyId: getUniqueKey(),
            category: getCheckIn(),
          },
        ];
      }
    },
    [values.transitionDate]
  );

  const initialEmpStatusDetails = useMemo(
    () => getDefaultValue(values.engagementStatusName),
    [values.engagementStatusName, getDefaultValue]
  );
  const sourceKey = `${values.engagementStatusId || values.engagementStatusName}:${values.transitionDate}`;
  const sourceEmpStatusDetails = useMemo<EmployeeStatusDetail[]>(
    () => initialEmpStatusDetails || [],
    [initialEmpStatusDetails]
  );
  const [draftState, setDraftState] = useState<{ sourceKey: string; empStatusDetails: EmployeeStatusDetail[] }>(() => ({
    sourceKey,
    empStatusDetails: sourceEmpStatusDetails,
  }));
  const empStatusDetails = draftState.sourceKey === sourceKey ? draftState.empStatusDetails : sourceEmpStatusDetails;
  const setEmpStatusDetails = useCallback(
    (value: EmployeeStatusDetailStateAction) => {
      setDraftState((prev) => {
        const currentEmpStatusDetails = prev.sourceKey === sourceKey ? prev.empStatusDetails : sourceEmpStatusDetails;
        const nextEmpStatusDetails = typeof value === 'function' ? value(currentEmpStatusDetails) : value;

        return {
          sourceKey,
          empStatusDetails: nextEmpStatusDetails,
        };
      });
    },
    [sourceEmpStatusDetails, sourceKey]
  );

  const onAdd = () => {
    setEmpStatusDetails((prev) => [
      ...prev,
      {
        keyId: getUniqueKey(),
        category: values.engagementStatusName === FIXED_TERM_CONTRACT ? getCheckIn(empStatusDetails) : '',
        date: '',
      },
    ]);
  };

  const handleDelete = (id: number) => {
    const filteredArray = empStatusDetails.filter((item) => {
      return item.keyId !== id;
    });

    setEmpStatusDetails(filteredArray);
  };

  useEffect(() => {
    setFieldValue(
      'empStatusDetails',
      empStatusDetails.map(({ id, category, date }) => ({
        id,
        category,
        date,
      }))
    );
  }, [empStatusDetails, setFieldValue]);

  return (
    <div className="emp-status-details-form custom-scroll-bar">
      <div className="emp-status-details-form__heading">Add {values.engagementStatusName} Category</div>
      <div className="emp-status-details-form__body">
        {empStatusDetails?.map((item, index) => (
          <AddEmployeeStatusDetails
            key={item.keyId}
            item={item}
            setEmpStatusDetails={setEmpStatusDetails}
            handleDelete={() => handleDelete(item.keyId)}
            values={values}
            employee={employee}
            currentFiscalYear={currentFiscalYear}
            type={values.engagementStatusName}
            index={index}
            touched={touched}
            submitCount={submitCount}
            errors={errors}
          />
        ))}
      </div>

      <div className="emp-status-details-form__add-button" onClick={() => onAdd()}>
        <FiPlusCircle className="emp-status-details-form__add-button--plus-icon" />
        <p className="emp-status-details-form__button-label">Add Another Category</p>
      </div>
    </div>
  );
};

export default EmployeeStatusDetails;
